import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  orendaUserManager,
  hardLogoutOrenda,
  isSigningOut,
} from "./orendaUserManager";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";
import { getOrendaSessionStatus } from "./orendaAuthStorage";
import orendaService from "../../services/orenda.service";
import { parseApplicationResponse } from "./orendaApplicationHelpers";
import OrendaShell from "./OrendaShell";
import OrendaLegalAgreements from "./OrendaLegalAgreements";
import OrendaKycInternal from "./OrendaKycInternal";
import OrendaSumsub from "./OrendaSumsub";
import OrendaCreateCard from "./OrendaCreateCard";
import OrendaDashboard from "./OrendaDashboard";
import OrendaStepPlaceholder from "./OrendaStepPlaceholder";

// Landing for IssuerOrenda users. Every phase renders inside OrendaShell so
// the layout (branded left panel + progress stepper) is consistent.
//
//   1. Session invalid → auto-signinRedirect to Cognito
//   2. Session valid → fetch viewapplication → dispatch by currentStep

const OrendaLanding = () => {
  const [phase, setPhase] = useState("checking-session");
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const redirectingRef = useRef(false);

  // Shared "the session died, bounce to Cognito" handler. We don't just
  // render "Session ended" and wait for a button click — the session is
  // already gone, so an auto-redirect is the right UX. If signinRedirect
  // itself throws (e.g. network), fall back to the manual CTA screen.
  const onAuthExpired = useCallback(() => {
    setError({
      kind: "auth-expired",
      message: "Your session has ended. Signing you in again…",
    });
    setPhase("error");
    orendaUserManager
      .signinRedirect({
        extraQueryParams: {
          identity_provider: orendaOidcConfig.identity_provider,
          prompt: "login",
        },
      })
      .catch(() => {
        // Leave the user on the manual CTA screen — the auth-expired kind
        // already renders a "Sign in again" button they can click.
      });
  }, []);

  const fetchApplication = useCallback(async () => {
    // Suppress every outcome if the user has just clicked sign-out — the
    // browser is about to navigate away. Showing an error flash before the
    // redirect lands is pure noise.
    if (isSigningOut()) {
      setPhase("signing-out");
      return;
    }
    setPhase("fetching-app");
    setError(null);
    try {
      const response = await orendaService.viewApplicationOrenda();
      if (isSigningOut()) return;
      // Bopayments wraps every response in a GenericApiResponse envelope
      // and returns HTTP 200 even on auth failures — the real status
      // lives in `payload.statusCode` + `payload.isSuccess`. Trust the
      // envelope over the HTTP status.
      const envelopeStatus = response?.payload?.statusCode;
      const envelopeOk = response?.payload?.isSuccess === true;
      if (!envelopeOk && envelopeStatus === 401) {
        // Session died upstream (Cognito, Orenda). Route the same way as
        // a thrown SsoSessionExpired.
        onAuthExpired();
        return;
      }
      setParsed(parseApplicationResponse(response));
      setPhase("ready");
    } catch (err) {
      if (isSigningOut()) return;
      // Translate raw transport errors into something a cardholder can act on.
      const status = err?.response?.status;
      // ensureOrendaTokensFresh throws SsoSessionExpired; axios can wrap
      // things in AxiosError and lose the inner `code`. Check both.
      const sessionExpired =
        err?.code === "SSO_SESSION_EXPIRED" ||
        err?.cause?.code === "SSO_SESSION_EXPIRED" ||
        err?.name === "SsoSessionExpired";
      if (sessionExpired || status === 401) {
        onAuthExpired();
        return;
      }
      const friendly =
        status === 403
          ? {
              kind: "forbidden",
              message:
                "We can't load your application right now. If this keeps happening, please contact support.",
            }
          : status >= 500
          ? {
              kind: "server",
              message:
                "We're having trouble reaching the server. Please try again in a moment.",
            }
          : {
              kind: "generic",
              message:
                err?.response?.data?.errorMessage ||
                "Something went wrong loading your application.",
            };
      setError(friendly);
      setPhase("error");
    }
  }, [onAuthExpired]);

  useEffect(() => {
    const status = getOrendaSessionStatus();
    if (!status.valid) {
      if (redirectingRef.current) return;
      redirectingRef.current = true;
      setPhase("signing-in");
      orendaUserManager
        .signinRedirect({
          extraQueryParams: {
            identity_provider: orendaOidcConfig.identity_provider,
            prompt: "login",
          },
        })
        .catch((err) => {
          setError({ message: err?.message || String(err) });
          setPhase("error");
        });
      return;
    }
    fetchApplication();
  }, [fetchApplication]);

  const currentStep = parsed?.status?.currentStep;

  // Once the user reaches the DashBoard step the UI is a full product
  // dashboard, not an onboarding wizard — bypass OrendaShell entirely so
  // OrendaDashboard owns the whole viewport (its own nav rail + right rail).
  const onDashboard =
    currentStep === "DashBoard" ||
    currentStep === "Dashboard" ||
    currentStep === "DASHBOARD";
  if (phase === "ready" && onDashboard && parsed) {
    return (
      <OrendaDashboard
        application={parsed.application}
        status={parsed.status}
        raw={parsed.raw}
        cachedCardDetails={parsed.cardDetails}
        onAdvance={fetchApplication}
      />
    );
  }

  // Everything else still runs inside the onboarding shell.
  const pagename = ["Welcome to", <br key="br" />, "Bopayments"];
  const pagemsg = "Complete a few quick steps to activate your card.";

  return (
    <OrendaShell
      currentStep={currentStep ?? null}
      pagename={pagename}
      pagemsg={pagemsg}
    >
      {phase === "checking-session" && (
        <div className="orenda-card">Checking your session…</div>
      )}

      {phase === "signing-in" && (
        <>
          <h2 className="orenda-heading">Redirecting you to sign in</h2>
          <p className="orenda-subheading">
            You'll be sent to Orenda's secure sign-in page. If nothing happens,
            use the button below.
          </p>
          <button
            onClick={hardLogoutOrenda}
            className="orenda-btn-primary"
          >
            Sign in with Orenda
          </button>
        </>
      )}

      {phase === "fetching-app" && (
        <div className="orenda-card">Loading your application…</div>
      )}

      {phase === "signing-out" && (
        <div className="orenda-card">Signing you out…</div>
      )}

      {phase === "error" && error?.kind === "auth-expired" && (
        <>
          <h2 className="orenda-heading">Session ended</h2>
          <p className="orenda-subheading">
            For your security, we need you to sign in again.
          </p>
          <div className="orenda-actions">
            <button
              onClick={() => {
                orendaUserManager
                  .signinRedirect({
                    extraQueryParams: {
                      identity_provider: orendaOidcConfig.identity_provider,
                      prompt: "login",
                    },
                  })
                  .catch(() => {});
              }}
              className="orenda-btn-primary"
            >
              Sign in again
            </button>
          </div>
        </>
      )}

      {phase === "error" && error?.kind !== "auth-expired" && (
        <>
          <h2 className="orenda-heading">
            {error?.kind === "server"
              ? "We're having trouble connecting"
              : "Something went wrong"}
          </h2>
          <p className="orenda-subheading">
            {error?.message || "An unexpected error occurred."}
          </p>
          <div className="orenda-actions">
            <button onClick={fetchApplication} className="orenda-btn-primary">
              Try again
            </button>
          </div>
        </>
      )}

      {phase === "ready" && parsed && (
        <StepRouter parsed={parsed} onAdvance={fetchApplication} />
      )}
    </OrendaShell>
  );
};

// Orenda v2 onboarding step router.
// Docs: https://api-docs.orenda-apps.com/onboarding/overview/
//
// Action-required steps map to a dedicated screen; waiting/terminal steps map
// to the placeholder which polls and shows the right messaging. Legacy v1
// step names (KYC_INTERNAL, KYC_SUMSUB, RISK, HOLDER_CREATION, ...) are kept
// as aliases so we don't break if the backend returns either during the
// transition.
const StepRouter = ({ parsed, onAdvance }) => {
  const step = parsed.status?.currentStep;
  switch (step) {
    case "LEGAL_AGREEMENTS":
      return (
        <OrendaLegalAgreements
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    case "KYC_FORM":
    case "KYC_INTERNAL":                     // v1 alias
    case "KYC_INTERNAL_VALIDATION_ERRORS":   // v1 alias (retry)
      return (
        <OrendaKycInternal
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    case "IDENTITY_VERIFICATION":
    case "KYC_SUMSUB":                       // v1 alias
      return (
        <OrendaSumsub
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    // COMPLETED is the trigger for the final onboarding action: issue the
    // applicant's first card. OrendaCreateCard reads sessionStorage so a
    // user who already created a card and refreshes will skip the CTA.
    case "COMPLETED":
      return (
        <OrendaCreateCard
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          onAdvance={onAdvance}
        />
      );
    // DashBoard — set by the backend once the card is issued. User lands
    // here on every subsequent visit to the Orenda app.
    case "DashBoard":
    case "Dashboard":
    case "DASHBOARD":
      return (
        <OrendaDashboard
          application={parsed.application}
          status={parsed.status}
          raw={parsed.raw}
          cachedCardDetails={parsed.cardDetails}
          onAdvance={onAdvance}
        />
      );
    // Waiting and terminal states — the placeholder decides what to render
    // based on currentStep and whether to keep polling.
    case "PROCESSING":
    case "IN_REVIEW":
    case "DECLINED":
    case "ERROR":
    case "ADD_UBOS":                         // company-only; render placeholder for now
    case "PROVIDER_DOCUMENTS":               // rare; placeholder with support contact
    // v1 aliases that collapse into v2 waiting / terminal states:
    case "KYC":
    case "RISK":
    case "HOLDER_CREATION":
    case "ACCOUNT_CREATION":
    default:
      return (
        <OrendaStepPlaceholder
          application={parsed.application}
          status={parsed.status}
          onAdvance={onAdvance}
        />
      );
  }
};

export default OrendaLanding;
